import { History, MessageSquare, PanelRightClose } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { PanelSection } from '../../lib/panelPrefs';

interface ActivityPanelHeaderProps {
  section: PanelSection;
  /** Whole-record total. Null while the first page is still in flight — the pill is then absent, not zero. */
  count: number | null;
  onCollapse: () => void;
}

export function ActivityPanelHeader({ section, count, onCollapse }: ActivityPanelHeaderProps) {
  const isActivity = section === 'activity';
  const Icon = isActivity ? History : MessageSquare;

  return (
    <header className="flex items-center gap-2 border-b border-border p-3">
      <Icon className="h-[15px] w-[15px] shrink-0 text-muted-foreground" aria-hidden />

      <h2 className="text-sm font-bold text-foreground">{isActivity ? 'Activity' : 'Comments'}</h2>

      {isActivity
        ? // Absent until the count is known, rather than a placeholder `0`. A zero pill on a record that
          // simply has not loaded yet says something false, and it says it in the one place a reader looks
          // to judge whether the panel is worth opening.
          count !== null && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
              {count}
            </span>
          )
        : (
          <span className="rounded-full bg-info/10 px-1.5 py-0.5 text-[11px] font-medium text-info">
            Soon
          </span>
        )}

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Collapse — press A to reopen"
            onClick={onCollapse}
            className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PanelRightClose className="h-4 w-4" aria-hidden />
          </button>
        </TooltipTrigger>
        {/* Same wording as the aria-label, verbatim — the gate requires both, and two phrasings of one
            action is how a screen-reader user and a sighted user end up describing different buttons. */}
        <TooltipContent side="left">Collapse — press A to reopen</TooltipContent>
      </Tooltip>
    </header>
  );
}
